import { env } from './env';

/**
 * Which surface this portal talks to.
 *
 * The server mounts five, and a token is only valid for its own - so pointing
 * this at another surface would fail with 403 rather than silently working.
 *
 * The refresh cookie is named and path-scoped per surface server-side
 * (`nudent_refresh_production`, `/api/production/auth`), which is what lets an
 * engineer be signed into this portal and the admin console in the same browser
 * without one ending the other's session.
 *
 * Note this surface also sits behind an IP allowlist server-side. A request
 * from outside the allowed networks is refused before it reaches login, so a
 * blank screen in a new office is a firewall question, not a bug here.
 */
export const API = {
  baseUrl: env.apiBaseUrl,
  surface: '/api/production',
  timeoutMs: 20_000,

  endpoints: {
    login: '/auth/login',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    me: '/auth/me',
    sessions: '/auth/sessions',
    revokeOtherSessions: '/auth/sessions/revoke-others',
    // Second factor. `mfaVerify` completes a login and so takes no session;
    // the rest manage the factor of whoever is signed in.
    mfaVerify: '/auth/mfa/verify',
    mfa: '/auth/mfa',
    mfaEnroll: '/auth/mfa/enroll',
    mfaEnrollConfirm: '/auth/mfa/enroll/confirm',
    mfaDisable: '/auth/mfa/disable',

    dashboard: '/dashboard',
    logs: '/logs',
    crashes: '/crashes',
    crashGroups: '/crashes/groups',
    issues: '/issues',
    issue: (id: string) => `/issues/${id}`,
    versions: '/versions',
    version: (id: string) => `/versions/${id}`,
    versionAdoption: '/versions/adoption',
    deployments: '/deployments',
    deployment: (id: string) => `/deployments/${id}`,
    deploymentApprove: (id: string) => `/deployments/${id}/approve`,
    settings: '/settings',
    settingsTestPage: '/settings/test-page',
  },
} as const;

export const apiUrl = (path: string): string => `${API.baseUrl}${API.surface}${path}`;
